import * as React from 'react';
import { graphql } from 'gatsby';
import { FileNode, ResponsiveImage, SvgImage } from 'lib/types';
import classnames from 'classnames';
import ChevronRight from 'booking/components/DateForm/assets/chevron-right.inline.svg';
import { EmployeeData } from '@bondvet/types/clinicTeam';
import { MEDICAL_ROLES } from 'core/constants';
import ImageWrapper from 'core/components/ImageWrapper';
import * as styles from './EmployeeView.module.scss';

export type EmployeeImageWithModal = ResponsiveImage & {
    desktopModal?: FileNode;
    mobileModal?: FileNode;
};

export type Employee = EmployeeData & {
    id: string;
    image: EmployeeImageWithModal | SvgImage | null;
    alt?: string;
    description?: string | JSX.Element | JSX.Element[];
    longDescription?: string | JSX.Element | JSX.Element[];
    paragraph1?: string | JSX.Element | JSX.Element[];
    paragraph2?: string | JSX.Element | JSX.Element[];
    quote?: string | JSX.Element | JSX.Element[];
    sortOrder?: number;
};
type EmployeeViewProps = {
    employee: Employee;
    onClick: (employee: Employee) => void;
    showDescriptionOnHover?: boolean;
    openModalOnClick?: boolean;
    noDescription?: boolean;
    showChevron?: boolean;
    classes?: Partial<typeof styles>;
    loading?: 'eager' | 'lazy';
    customImage?: React.ReactNode;
};

function EmployeeView({
    employee,
    onClick,
    showDescriptionOnHover,
    openModalOnClick,
    noDescription,
    showChevron = false,
    classes,
    loading,
    customImage,
}: EmployeeViewProps) {
    const handleClick = React.useCallback(() => {
        if (onClick) {
            onClick(employee);
        }
    }, [onClick, employee]);

    const showDoctorPrefix = MEDICAL_ROLES.includes(employee.role);

    const content = (
        <>
            {!customImage && (
                <div
                    className={classnames(
                        styles.imageWrapper,
                        classes?.imageWrapper,
                    )}
                >
                    <ImageWrapper
                        className={classnames(styles.image, classes?.image)}
                        image={employee.image}
                        alt=""
                        loading={loading}
                    />
                    {employee.description && !noDescription && (
                        <div
                            className={classnames(
                                styles.showMore,
                                classes?.showMore,
                            )}
                        >
                            Show more
                        </div>
                    )}
                </div>
            )}
            {customImage}
            <div>
                <div
                    className={classnames(
                        styles.nameWrapper,
                        classes?.nameWrapper,
                    )}
                >
                    <h3 className={classnames(styles.name, classes?.name)}>
                        {`${showDoctorPrefix ? 'Dr. ' : ''}${employee.name}`}
                    </h3>
                    {showChevron && <ChevronRight role="none" />}
                </div>
                <p className={classnames(styles.role, classes?.role)}>
                    {employee.role}
                </p>
            </div>
            {employee.longDescription && !openModalOnClick && (
                <button
                    type="button"
                    className={classnames(styles.button, classes?.button)}
                    onClick={handleClick}
                >
                    Show Bio
                </button>
            )}
        </>
    );

    if (openModalOnClick) {
        return (
            <button
                onClick={handleClick}
                type="button"
                className={classnames(styles.employee, classes?.employee, {
                    [classnames(
                        styles.showDescOnHover,
                        classes?.showDescOnHover,
                    )]: showDescriptionOnHover,
                })}
            >
                {content}
            </button>
        );
    }
    return (
        <div className={classnames(styles.employee, classes?.employee)}>
            {content}
        </div>
    );
}

/* eslint-disable max-len */
export const query = graphql`
    fragment EmployeeModalImage on File {
        publicURL
        alt
        mobileModal: childImageSharp {
            gatsbyImageData(
                aspectRatio: 1
                sizes: "(max-width: 430px) calc(100vw - 60px), 527px"
                transformOptions: { fit: COVER, cropFocus: ATTENTION }
            )
        }
        desktopModal: childImageSharp {
            gatsbyImageData(
                width: 527
                height: 527
                sizes: "(max-width: 430px) calc(100vw - 60px), 527px"
                transformOptions: { fit: COVER, cropFocus: ATTENTION }
            )
        }
    }
    fragment EmployeeImage on File {
        mobile: childImageSharp {
            gatsbyImageData(
                aspectRatio: 1
                sizes: "(max-width: 767px) calc((100vw - 32px) / 1.5), (max-width: 899px) calc((100vw - 180px) / 2), (max-width: 1243px) calc((100vw - 196px) / 3), calc(1360px / 5)"
                transformOptions: { fit: COVER, cropFocus: ATTENTION }
            )
        }
        desktop: childImageSharp {
            gatsbyImageData(
                aspectRatio: 1
                sizes: "(max-width: 767px) calc((100vw - 32px) / 1.5), (max-width: 899px) calc((100vw - 180px) / 2), (max-width: 1243px) calc((100vw - 196px) / 3), calc(1360px / 5)"
                transformOptions: { fit: COVER, cropFocus: ATTENTION }
            )
        }
        ...EmployeeModalImage
    }
    fragment EmployeeData on Employee {
        id
        name
        role
        sortOrder
        medicalTeamSortOrder
        leadershipTeamSortOrder
        image {
            file {
                ...EmployeeImage
            }
        }
    }
    fragment EmployeeWithBioData on Employee {
        ...EmployeeData
        description
        longDescription
        paragraph1
        paragraph2
        quote
    }
`;
/* eslint-enable max-len */

export default React.memo(EmployeeView);
